import clsx from 'clsx';
import { Command } from 'cmdk';
import type { ComponentProps, FunctionComponent, ReactNode } from 'react';

import { IconSearch } from '../icons';
import { HIDDEN_SCROLLBAR_CLASS_NAME, useScrollFade } from '../scroll-fade/ScrollFade';

export const CommandPalette: FunctionComponent<ComponentProps<typeof Command>> = ({ className, ...props }) => (
    <Command className={clsx('flex flex-col min-h-0 outline-none', className)} {...props} />
);

export type CommandPaletteSearchProps = Pick<
    ComponentProps<typeof Command.Input>,
    'aria-label' | 'autoFocus' | 'onValueChange' | 'placeholder' | 'value'
>;

export const CommandPaletteSearch: FunctionComponent<CommandPaletteSearchProps> = (props) => (
    <div className="flex items-center gap-m px-xl border-b border-border-subtle text-text-content-subtle">
        <IconSearch size={16} />
        <Command.Input
            className="flex-1 min-w-0 h-11xl bg-transparent border-none outline-none font-sans text-body-s text-text-content-strong placeholder:text-text-content-subtle"
            {...props}
        />
    </div>
);

export const CommandPaletteList: FunctionComponent<{ readonly children: ReactNode; readonly className?: string }> = ({
    children,
    className,
}) => {
    const listFade = useScrollFade({ orientation: 'vertical' });

    return (
        <Command.List
            className={clsx(HIDDEN_SCROLLBAR_CLASS_NAME, 'flex-1 min-h-0 overflow-y-auto p-s outline-none', className)}
            ref={listFade.viewportRef}
            style={listFade.style}
        >
            {children}
        </Command.List>
    );
};

export const CommandPaletteEmpty: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <Command.Empty className="py-3xl px-l text-center text-body-s text-text-content-subtle">{children}</Command.Empty>
);

export const CommandPaletteGroup: FunctionComponent<ComponentProps<typeof Command.Group>> = ({
    className,
    ...props
}) => (
    <Command.Group
        className={clsx(
            '[&_[cmdk-group-heading]]:px-l [&_[cmdk-group-heading]]:pt-l [&_[cmdk-group-heading]]:pb-s',
            '[&_[cmdk-group-heading]]:text-body-xxs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-content-subtle',
            className,
        )}
        {...props}
    />
);

export const CommandPaletteItem: FunctionComponent<ComponentProps<typeof Command.Item>> = ({ className, ...props }) => (
    <Command.Item
        className={clsx(
            'flex items-center gap-m rounded-sm py-l px-l cursor-pointer select-none outline-none',
            'text-body-s font-medium text-text-content-strong',
            'data-[selected=true]:bg-surface-hover',
            className,
        )}
        {...props}
    />
);

export const CommandPaletteFooter: FunctionComponent<{ readonly children: ReactNode; readonly className?: string }> = ({
    children,
    className,
}) => <div className={clsx('shrink-0 p-s border-t border-border-subtle', className)}>{children}</div>;
